import { useState, useEffect } from 'react';

// Tailwind breakpoints in pixels
const breakpoints = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536
} as const;

type Breakpoint = keyof typeof breakpoints;
type Value = Breakpoint | number;
type Query = 'up' | 'down' | 'between' | 'only';

function getBreakpointValue(value: Value): number {
  if (typeof value === 'number') {
    return value;
  }
  return breakpoints[value] || 0;
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);

    const handler = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

export default function useResponsive(query: Query, start?: Value, end?: Value): boolean {
  const startValue = start ? getBreakpointValue(start) : 0;
  const endValue = end ? getBreakpointValue(end) : 0;

  const mediaUp = useMediaQuery(`(min-width: ${startValue}px)`);
  const mediaDown = useMediaQuery(`(max-width: ${startValue - 0.02}px)`);
  const mediaBetween = useMediaQuery(
    `(min-width: ${startValue}px) and (max-width: ${endValue - 0.02}px)`
  );

  // For 'only', we need to find the next breakpoint
  const breakpointKeys = Object.keys(breakpoints) as Breakpoint[];
  const startIndex = typeof start === 'string' ? breakpointKeys.indexOf(start) : -1;
  const nextBreakpoint =
    startIndex >= 0 && startIndex < breakpointKeys.length - 1
      ? breakpoints[breakpointKeys[startIndex + 1]]
      : Infinity;
  const mediaOnly = useMediaQuery(
    `(min-width: ${startValue}px) and (max-width: ${nextBreakpoint - 0.02}px)`
  );

  switch (query) {
    case 'up':
      return mediaUp;
    case 'down':
      return mediaDown;
    case 'between':
      return mediaBetween;
    case 'only':
      return mediaOnly;
    default:
      return mediaOnly;
  }
}

// ----------------------------------------------------------------------

export function useWidth(): Breakpoint {
  const isSm = useMediaQuery(
    `(min-width: ${breakpoints.sm}px) and (max-width: ${breakpoints.md - 0.02}px)`
  );
  const isMd = useMediaQuery(
    `(min-width: ${breakpoints.md}px) and (max-width: ${breakpoints.lg - 0.02}px)`
  );
  const isLg = useMediaQuery(
    `(min-width: ${breakpoints.lg}px) and (max-width: ${breakpoints.xl - 0.02}px)`
  );
  const isXl = useMediaQuery(
    `(min-width: ${breakpoints.xl}px) and (max-width: ${breakpoints['2xl'] - 0.02}px)`
  );
  const is2xl = useMediaQuery(`(min-width: ${breakpoints['2xl']}px)`);

  if (is2xl) return '2xl';
  if (isXl) return 'xl';
  if (isLg) return 'lg';
  if (isMd) return 'md';
  if (isSm) return 'sm';
  return 'xs';
}
